'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '../../../lib/supabase/client';

type FormStep = 1 | 2 | 3;

interface FormData {
  gender: number;
  genderSearch: number;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  nickname: string;
  email: string;
  password: string;
  plz: string;
  land: number;
  city: string;
}

const STEPS = ['Über dich', 'Dein Profil', 'Abschluss'];

export default function RegistrationForm() {
  const [step, setStep] = useState<FormStep>(1);
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'facebook' | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [plzValidating, setPlzValidating] = useState(false);
  const [plzValid, setPlzValid] = useState<boolean | null>(null);
  const [cityName, setCityName] = useState<string>('');

  const [formData, setFormData] = useState<FormData>({
    gender: 0,
    genderSearch: 0,
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    nickname: '',
    email: '',
    password: '',
    plz: '',
    land: 105,
    city: '',
  });

  const updateField = (field: keyof FormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
    
    // Reset PLZ validation when PLZ or land changes
    if (field === 'plz' || field === 'land') {
      setPlzValid(null);
      setCityName('');
      setFormData(prev => ({ ...prev, city: '' }));
    }
  };

  // Validate PLZ in real-time using Zippopotam API directly
  const validatePlz = async (plz: string, land: number) => {
    if (!plz || plz.length < 4) {
      setPlzValid(null);
      setCityName('');
      return;
    }

    setPlzValidating(true);
    setPlzValid(null);
    setCityName('');
    
    try {
      // Map land codes to country codes
      let countryCode = 'de';
      if (land === 106) countryCode = 'at';
      if (land === 107) countryCode = 'ch';
      
      const response = await fetch(`https://api.zippopotam.us/${countryCode}/${plz}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.places && data.places.length > 0) {
          const city = data.places[0]['place name'];
          setPlzValid(true);
          setCityName(city);
          setFormData(prev => ({ ...prev, city }));
        } else {
          setPlzValid(false);
          setError('Diese Postleitzahl konnte nicht gefunden werden');
        }
      } else {
        setPlzValid(false);
        setError('Diese Postleitzahl ist ungültig oder existiert nicht');
      }
    } catch (err) {
      console.error('PLZ validation error:', err);
      setPlzValid(false);
      setError('Fehler bei der PLZ-Validierung. Bitte versuche es erneut.');
    } finally {
      setPlzValidating(false);
    }
  };

  const validateStep1 = () => {
    if (!formData.gender) return 'Bitte wähle dein Geschlecht.';
    if (!formData.genderSearch) return 'Bitte wähle, wen du suchst.';
    if (!formData.birthDay || !formData.birthMonth || !formData.birthYear) return 'Bitte gib dein vollständiges Geburtsdatum an.';
    const year = parseInt(formData.birthYear);
    if (year < 1900 || year > 2006) return 'Du musst mindestens 18 Jahre alt sein.';
    return '';
  };

  const validateStep2 = () => {
    if (!formData.nickname || formData.nickname.length < 3) return 'Nickname muss mindestens 3 Zeichen lang sein.';
    if (!/^[a-zA-Z0-9._-]+$/.test(formData.nickname)) return 'Nickname darf nur Buchstaben, Zahlen und . _ - enthalten.';
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) return 'Bitte gib eine gültige E-Mail-Adresse an.';
    if (!formData.password || formData.password.length < 6) return 'Passwort muss mindestens 6 Zeichen lang sein.';
    return '';
  };

  const validateStep3 = () => {
    if (!formData.plz || formData.plz.length < 4) return 'Bitte gib eine gültige Postleitzahl ein.';
    if (plzValid === false) return 'Diese Postleitzahl ist ungültig oder existiert nicht.';
    if (plzValid === null) return 'Bitte warte, während wir deine Postleitzahl überprüfen.';
    if (!cityName && plzValid === true) return 'Stadt konnte nicht ermittelt werden. Bitte versuche es erneut.';
    return '';
  };

  const handleNext = () => {
    let err = '';
    if (step === 1) err = validateStep1();
    if (step === 2) err = validateStep2();
    if (err) { setError(err); return; }
    setStep((prev) => (prev < 3 ? (prev + 1) as FormStep : prev));
  };

  const handleSubmit = async () => {
    let err = validateStep3();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gender: formData.gender,
          genderSearch: formData.genderSearch,
          birthDay: formData.birthDay,
          birthMonth: formData.birthMonth,
          birthYear: formData.birthYear,
          nickname: formData.nickname,
          email: formData.email,
          password: formData.password,
          plz: formData.plz,
          land: formData.land,
        }),
      });

      const data = await response.json();
      
      // Debug logging
      console.log('API Response Status:', response.status);
      console.log('API Response OK:', response.ok);
      console.log('API Response Data:', data);
      console.log('Data Status:', data.status);
      console.log('Data Info:', data.info);
      console.log('Data Message:', data.message);

      // Check for success - handle both response.ok and data.status
      if (data.status === 'success' || (response.ok && response.status === 200 && !data.info)) {
        console.log('Registration successful, showing success screen');
        setSuccess(true);
      } else {
        const errorMsg = data.info || data.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.';
        console.log('Setting error message:', errorMsg);
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError('Ein technischer Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setSocialLoading(provider);
    setError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Social Login fehlgeschlagen. Bitte versuche es erneut.');
      setSocialLoading(null);
    }
  };

  const progressWidth = step === 1 ? '33%' : step === 2 ? '66%' : '100%';

  if (success) {
    return (
      <div className="text-center py-12 px-6">
        {/* Success Icon */}
        <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center animate-bounce"
          style={{ background: 'linear-gradient(135deg, rgba(232,49,90,0.2) 0%, rgba(232,49,90,0.05) 100%)', border: '3px solid rgba(232,49,90,0.5)', boxShadow: '0 8px 32px rgba(232,49,90,0.3)' }}>
          <Icon name="CheckIcon" size={36} className="text-primary-DEFAULT" style={{ color: '#E8315A' } as React.CSSProperties} />
        </div>

        {/* Main Heading */}
        <h3 className="font-display text-3xl font-bold text-white mb-3">
          Vielen Dank für deine Registrierung!
        </h3>

        {/* Email Verification Message */}
        <div className="mb-6">
          <p className="text-base leading-relaxed mb-3" style={{ color: 'rgba(245,240,247,0.8)' }}>
            Bitte überprüfe dein E-Mail-Postfach.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,247,0.6)' }}>
            Wir haben dir eine Bestätigungsmail gesendet.
          </p>
          <p className="text-sm leading-relaxed mt-2" style={{ color: 'rgba(245,240,247,0.6)' }}>
            <span className="font-semibold" style={{ color: 'rgba(245,240,247,0.75)' }}>Schau auch im Spam-Ordner nach</span>, falls du sie nicht sofort findest.
          </p>
        </div>

        {/* Urgency Message */}
        <div className="rounded-2xl p-6 mb-6" 
          style={{ 
            background: 'linear-gradient(135deg, rgba(232,49,90,0.15) 0%, rgba(232,49,90,0.05) 100%)', 
            border: '1px solid rgba(232,49,90,0.3)',
            boxShadow: '0 4px 24px rgba(232,49,90,0.15)'
          }}>
          <div className="flex items-center justify-center gap-2 mb-2">
            <Icon name="BoltIcon" size={20} style={{ color: '#E8315A' } as React.CSSProperties} />
            <p className="font-bold text-lg" style={{ color: '#E8315A' }}>
              Bestätige schnell dein Profil!
            </p>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(245,240,247,0.65)' }}>
            Die Nachrichten warten sicher schon auf dich.
          </p>
        </div>

        {/* Additional Info */}
        <div className="space-y-3 text-xs" style={{ color: 'rgba(245,240,247,0.5)' }}>
          <div className="flex items-start gap-3 text-left max-w-md mx-auto">
            <Icon name="EnvelopeIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: 'rgba(232,49,90,0.6)' } as React.CSSProperties} />
            <p className="leading-relaxed">
              <span className="font-semibold" style={{ color: 'rgba(245,240,247,0.7)' }}>E-Mail nicht erhalten?</span>
              <br />
              Überprüfe deine E-Mail-Adresse und den Spam-Ordner.
            </p>
          </div>
          <div className="flex items-start gap-3 text-left max-w-md mx-auto">
            <Icon name="ClockIcon" size={18} className="flex-shrink-0 mt-0.5" style={{ color: 'rgba(232,49,90,0.6)' } as React.CSSProperties} />
            <p className="leading-relaxed">
              <span className="font-semibold" style={{ color: 'rgba(245,240,247,0.7)' }}>Schnell sein lohnt sich!</span>
              <br />
              Je schneller du bestätigst, desto schneller kannst du loslegen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          {STEPS.map((label, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className={`step-dot ${step > i ? 'active' : ''}`} />
              <span className="text-xs font-medium hidden sm:block"
                style={{ color: step > i ? 'rgba(245,240,247,0.7)' : 'rgba(245,240,247,0.25)' }}>
                {label}
              </span>
            </div>
          ))}
          <span className="text-xs font-medium" style={{ color: 'rgba(245,240,247,0.4)' }}>
            {step}/3
          </span>
        </div>
        <div className="form-progress">
          <div className="form-progress-fill" style={{ width: progressWidth }} />
        </div>
      </div>

      {/* Step 1: Gender + Birthdate */}
      {step === 1 && (
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Ich bin
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => updateField('gender', 2)}
                className={`gender-btn rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 ${formData.gender === 2 ? 'active' : ''}`}
              >
                Frau
              </button>
              <button
                type="button"
                onClick={() => updateField('gender', 1)}
                className={`gender-btn rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 ${formData.gender === 1 ? 'active' : ''}`}
              >
                Mann
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Ich suche
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => updateField('genderSearch', 1)}
                className={`gender-btn rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 ${formData.genderSearch === 1 ? 'active' : ''}`}
              >
                Männer
              </button>
              <button
                type="button"
                onClick={() => updateField('genderSearch', 2)}
                className={`gender-btn rounded-xl py-3.5 text-sm font-semibold flex items-center justify-center gap-2 ${formData.genderSearch === 2 ? 'active' : ''}`}
              >
                Frauen
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Geburtsdatum
            </label>
            <div className="grid grid-cols-3 gap-2">
              <select
                value={formData.birthDay}
                onChange={e => updateField('birthDay', e.target.value)}
                className="form-input rounded-xl px-3 py-3 text-sm"
              >
                <option value="">Tag</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
              <select
                value={formData.birthMonth}
                onChange={e => updateField('birthMonth', e.target.value)}
                className="form-input rounded-xl px-3 py-3 text-sm"
              >
                <option value="">Monat</option>
                {['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                value={formData.birthYear}
                onChange={e => updateField('birthYear', e.target.value)}
                className="form-input rounded-xl px-3 py-3 text-sm"
              >
                <option value="">Jahr</option>
                {Array.from({ length: 88 }, (_, i) => 2006 - i).map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Profile */}
      {step === 2 && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Dein Nickname
            </label>
            <input
              type="text"
              placeholder="z.B. max_mustermann"
              value={formData.nickname}
              onChange={e => updateField('nickname', e.target.value)}
              maxLength={15}
              className="form-input w-full rounded-xl px-4 py-3 text-sm"
            />
            <p className="text-xs mt-1.5" style={{ color: 'rgba(245,240,247,0.3)' }}>
              3–15 Zeichen, nur Buchstaben, Zahlen und . _ -
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              E-Mail-Adresse
            </label>
            <input
              type="email"
              placeholder="deine@email.de"
              value={formData.email}
              onChange={e => updateField('email', e.target.value)}
              className="form-input w-full rounded-xl px-4 py-3 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Passwort
            </label>
            <input
              type="password"
              placeholder="Mindestens 6 Zeichen"
              value={formData.password}
              onChange={e => updateField('password', e.target.value)}
              className="form-input w-full rounded-xl px-4 py-3 text-sm"
            />
          </div>
        </div>
      )}

      {/* Step 3: Location */}
      {step === 3 && (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Land
            </label>
            <select
              value={formData.land}
              onChange={e => updateField('land', parseInt(e.target.value))}
              className="form-input w-full rounded-xl px-4 py-3 text-sm"
            >
              <option value={105}>🇩🇪 Deutschland</option>
              <option value={106}>🇦🇹 Österreich</option>
              <option value={107}>🇨🇭 Schweiz</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-2"
              style={{ color: 'rgba(245,240,247,0.5)' }}>
              Postleitzahl
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="z.B. 10115"
                value={formData.plz}
                onChange={e => {
                  const newPlz = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('plz', newPlz);
                  // Auto-validate as user types
                  if (newPlz.length >= 4) {
                    validatePlz(newPlz, formData.land);
                  }
                }}
                onBlur={() => {
                  if (formData.plz.length >= 4) {
                    validatePlz(formData.plz, formData.land);
                  }
                }}
                className="form-input w-full rounded-xl px-4 py-3 pr-10 text-sm"
              />
              {plzValidating && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <svg className="animate-spin w-4 h-4" style={{ color: 'rgba(245,240,247,0.4)' }} viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                </div>
              )}
              {!plzValidating && plzValid === true && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Icon name="CheckIcon" size={16} style={{ color: '#22C55E' } as React.CSSProperties} />
                </div>
              )}
              {!plzValidating && plzValid === false && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Icon name="XMarkIcon" size={16} style={{ color: '#EF4444' } as React.CSSProperties} />
                </div>
              )}
            </div>
            <p className="text-xs mt-1.5" style={{ color: 'rgba(245,240,247,0.3)' }}>
              Damit zeigen wir dir Singles in deiner Nähe.
            </p>
            {cityName && plzValid && (
              <div className="mt-2 px-3 py-2 rounded-lg text-sm font-medium" 
                style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#22C55E' }}>
                <span className="inline-block mr-1">✓</span> {cityName}
              </div>
            )}
          </div>
          <div className="rounded-xl p-4 text-xs leading-relaxed"
            style={{ background: 'rgba(232,49,90,0.06)', border: '1px solid rgba(232,49,90,0.15)', color: 'rgba(245,240,247,0.45)' }}>
            Mit der Registrierung stimmst du unseren{' '}
            <a href="#" className="underline hover:text-white transition-colors">AGB</a> und der{' '}
            <a href="#" className="underline hover:text-white transition-colors">Datenschutzerklärung</a> zu.
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-4 rounded-xl px-4 py-3 text-sm"
          style={{ background: 'rgba(232,49,90,0.1)', border: '1px solid rgba(232,49,90,0.3)', color: '#FF8CA8' }}>
          {error}
        </div>
      )}

      {/* Navigation */}
      <div className="mt-6 flex gap-3">
        {step > 1 && (
          <button
            type="button"
            onClick={() => setStep(prev => (prev - 1) as FormStep)}
            className="flex-1 py-3.5 rounded-xl text-sm font-semibold transition-all"
            style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(245,240,247,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            Zurück
          </button>
        )}
        <button
          type="button"
          onClick={step < 3 ? handleNext : handleSubmit}
          disabled={loading}
          className={`btn-primary py-3.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${step === 1 ? 'w-full' : 'flex-[2]'}`}
        >
          {loading ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Wird verarbeitet...
            </>
          ) : step < 3 ? (
            <>Weiter <span>→</span></>
          ) : (
            <>Kostenlos anmelden ✨</>
          )}
        </button>
      </div>

      {/* Social Login Buttons */}
      <div className="mt-4 space-y-3">
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
          <span className="text-xs font-medium" style={{ color: 'rgba(245,240,247,0.35)' }}>oder mit Social Login</span>
          <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
        </div>

        <a
          href="https://xkuss.com/xgoogle.php?wat=go"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
          style={{
            background: 'rgba(255,255,255,0.95)',
            color: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.2)',
            textDecoration: 'none',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Mit Google registrieren
        </a>

        <a
          href="https://xkuss.com/xface.php?wat=go"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 active:scale-[0.98]"
          style={{
            background: '#1877F2',
            color: '#ffffff',
            border: '1px solid rgba(24,119,242,0.3)',
            textDecoration: 'none',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#ffffff">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          Mit Facebook registrieren
        </a>
      </div>
    </div>
  );
}